import { useEffect, useState } from 'react';

/**
 * usePortraitOrientation — reports whether the viewport is currently in
 * portrait orientation (height ≥ width). Used by the tablet dental chart
 * to swap arch geometry and rail compaction between portrait and
 * landscape on coarse-pointer devices that rotate freely.
 *
 * Wraps `window.matchMedia('(orientation: portrait)')` with the same
 * factory-init + stable-subscription pattern as `useCoarsePointer` so
 * SSR / private mode / Safari < 14 all behave. Falls back to comparing
 * innerHeight/innerWidth if matchMedia throws.
 */
export default function usePortraitOrientation() {
  const [isPortrait, setIsPortrait] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    try { return window.matchMedia('(orientation: portrait)').matches; }
    catch (_e) {
      try { return window.innerHeight >= window.innerWidth; }
      catch (_e2) { return false; }
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    let mq;
    try { mq = window.matchMedia('(orientation: portrait)'); }
    catch (_e) { return undefined; }

    const onChange = (e) => setIsPortrait(!!e.matches);
    setIsPortrait(mq.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14 / legacy fallback.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isPortrait;
}
